import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  ICampaignAttributes,
  IEvent,
  IFlight,
  IGoal,
  TAttributionModel,
} from '@feathr/blackbox';
import { Table } from '@feathr/components';
import type { DisplayModel } from '@feathr/rachis';

import { useGoalsTableState } from './GoalsTable.hooks';
import type { IConversionStats, IGoalRow } from './GoalsTable.utils';
import { paginateData, sortGoalsData, transformGoalsToRows } from './GoalsTable.utils';
import { GoalsTableColumns } from './GoalsTableColumns';

interface IProps {
  attributionModel: TAttributionModel;
  goals: IGoal[];
  model: DisplayModel<ICampaignAttributes> | DisplayModel<IEvent> | DisplayModel<IFlight>;
}

function GoalsTable({ attributionModel, goals, model }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { sort, page, pageSize, handleSortChange, handlePageChange, handlePageSizeChange } =
    useGoalsTableState();

  const modelAttrs = model.toJS();
  const stats = 'stats_cache' in modelAttrs ? modelAttrs.stats_cache : undefined;
  const conversionStats = stats?.conversions?.[attributionModel] as IConversionStats | undefined;

  const goalsData = transformGoalsToRows(goals, conversionStats);
  const sortedData = sortGoalsData(goalsData, sort);
  const { paginatedData, totalPages } = paginateData(sortedData, page, pageSize);

  const columns = GoalsTableColumns();

  return (
    <Table<IGoalRow>
      columns={columns}
      idKey={'id'}
      initialPagesize={5}
      initialSort={[{ id: 'name' }]}
      isPaginated={true}
      items={paginatedData}
      noDataText={t('No Goals')}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onSortChange={handleSortChange}
      page={page}
      pages={totalPages}
      pageSize={pageSize}
      sort={sort}
    />
  );
}

export default observer(GoalsTable);
